import '@/theme/partials/_button.scss';

import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import {
  InputChangeEventDetail,
  IonButton,
  IonButtons,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCheckbox,
  IonInput,
  IonText,
  IonToolbar,
} from '@ionic/react';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import ENVIRONMENT from '@/environments/environment';
import { storageService } from '@/services/storage';
import { usernameValidation } from '@/utils/validators';

import styles from './LoginUsername.module.scss';

interface LoginUsernameProps {
  nextBtn: (username: string) => void;
  onUsernameChange: (username: string) => void;
  username: string;
  userError: boolean;
  disabled?: boolean;
}

export default function LoginUsername({
  nextBtn,
  onUsernameChange,
  username,
  userError,
  disabled,
}: LoginUsernameProps) {
  const [user, setUser] = useState<string>('');
  const [userValidated, setUserValidated] = useState<boolean>(false);
  const [errorColor, setErrorColor] = useState<string>('accessInput');
  const [checked, setChecked] = useState<boolean>(false);
  const [loginCookie] = useState(storageService.hasPepperMerchantLogin);

  const handleVerifyUserName = (value: string) => {
    onUsernameChange(value);
    const validated = usernameValidation(value);
    if (validated) {
      setUserValidated(true);
      setErrorColor('accessInputValidate');
    } else if (validated === null && !userError) {
      setUserValidated(false);
      setErrorColor('accessInput');
    } else {
      setUserValidated(false);
      setErrorColor('accessInputError');
    }
  };
  const changeCheckboxStatus = (checkValue: boolean) => {
    setChecked(checkValue);
    if (checkValue) {
      storageService.setPepperMerchantLogin(true);
    }
  };

  useEffect(() => {
    if (username) {
      setUser(username);
      handleVerifyUserName(username);
    }
  }, [username]);

  useEffect(() => {
    if (loginCookie) {
      setChecked(true);
    }
  }, []);

  const openTerms = () => {
    Browser.open({ url: ENVIRONMENT.URL_TERMS || 'https://peppercomercios.pepperfinance.es/terms-of-use' });
  };

  const openPrivacy = () => {
    Browser.open({ url: ENVIRONMENT.URL_PRIVACY_POLICY || 'https://peppercomercios.pepperfinance.es/privacy-policy' });
  };

  const handleInputChange = (event: CustomEvent<InputChangeEventDetail>) => {
    const value = event.detail.value || '';
    let newValue = '';

    for (let i = 0; i < 3; i++) {
      if (/[A-Za-z]/.test(value[i] || '')) {
        newValue += value[i].toUpperCase();
      }
    }
    for (let i = 3; i < 9; i++) {
      if (/[0-9]/.test(value[i] || '')) {
        newValue += value[i];
      }
    }
    setUser(newValue);
    handleVerifyUserName(newValue);
  };
  const isApp = Capacitor.isNativePlatform();
  return (
    <>
      <IonCardHeader mode="md">
        <IonCardTitle className={styles.LoginUsernameHeaderTitle} mode="md">¡Hola!</IonCardTitle>
        {ENVIRONMENT.ENV === 'development' && isApp && <IonCardTitle className={styles.textPlay} mode="md">ES DEV</IonCardTitle>}
        <IonCardSubtitle className={styles.LoginUsernameHeaderSubtitle} mode="md">
          Introduce tu usuario para que podamos verificar que eres tú.
        </IonCardSubtitle>
      </IonCardHeader>
      <IonCardContent className={styles.LoginUsernameCardContent}>
        <form
          action=""
          onSubmit={(e) => {
            e.preventDefault();
            (user?.length === 8 || user?.length === 9)
              && checked && nextBtn(user);
          }}
        >
          <IonText className={styles.LoginUsernameContentTitle}>
            Escribe tu usuario
          </IonText>
          <IonInput
            placeholder="Ej: XXX00000"
            type="text"
            value={user}
            className={classNames(
              styles.inputUserName,
              errorColor === 'accessInput' && styles.accessInput,
              errorColor === 'accessInputValidate' && styles.accessInputValidate,
              errorColor === 'accessInputError' && styles.accessInputError,
            )}
            onIonInput={handleInputChange}
            maxlength={9}
            clearInput
          />
          <div className={classNames(styles.loginCheckbox, styles.consentCheckWrapper)}>
            <IonCheckbox
              slot="start"
              onIonChange={() => changeCheckboxStatus(!checked)}
              checked={checked}
            />
            <div className={styles.termsLabel}>
              <p className={styles.terms}>
                He leído y consiento los
                {' '}
                <span
                  className={styles.termsUnderline}
                  onClick={() => openTerms()}
                >
                  Términos de Uso
                  {' '}
                </span>
                {' '}
                y la
                <span
                  className={styles.termsUnderline}
                  onClick={() => openPrivacy()}
                >
                  {' '}
                  Política de Privacidad
                  {' '}
                </span>
                de Pepper Comercios.
              </p>
            </div>
          </div>

          <IonToolbar className={styles.LoginUsernameContentToolbar}>
            <IonButtons>
              <IonButton
                className="nextButton"
                type="submit"
                disabled={!userValidated || !checked || disabled}
              >
                Siguiente
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </form>
      </IonCardContent>
    </>
  );
}
